/*
 * THESIS: the offer page is the proposal, drawn: three phases as a flow, the
 *   artifacts as a ledger, the year as a cadence ring, refusing the benefits
 *   card wall.
 * OWN-WORLD: Maestro grammar in cyan (strategy's accent): duration chips,
 *   flow-dash connectors between phases, a 12-dot annual ring drawn in SVG.
 * STORY: an executive recognizes the Porter structure, sees the artifacts
 *   they will hold, understands the year-long rhythm, opens a card.
 * FIRST VIEWPORT: kicker, promise headline, the Hoshin method line, the
 *   three-phase flow beginning immediately.
 * FORM: phase flow + artifact ledger + cadence ring; pinned direction.
 */
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Seo from '../Seo'
import SiteAmbient from '../common/SiteAmbient'
import trackEvent from '../../utils/trackEvent'
import { getCtaContent } from '../../content/ctaContent'
import { getStrategicPlanningContent } from '../../content/strategicPlanningContent'

function useReveal(threshold = 0.1) {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    if (typeof IntersectionObserver === 'undefined') {
      el.classList.add('is-visible')
      return undefined
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible')
          obs.disconnect()
        }
      },
      { threshold }
    )
    obs.observe(el)
    // failsafe: if the observer never fires (hidden tab, odd engine states),
    // the section still reveals instead of holding opacity 0 forever
    const failsafe = setTimeout(() => { el.classList.add('is-visible'); obs.disconnect() }, 2600)
    return () => { obs.disconnect(); clearTimeout(failsafe) }
  }, [threshold])
  return ref
}

// The year as an instrument: twelve review dots around a ring.
const CadenceRing = () => (
  <svg viewBox="0 0 200 200" className="h-44 w-44 text-pyramid-19" aria-hidden="true">
    <circle cx="100" cy="100" r="78" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
    {Array.from({ length: 12 }).map((_, i) => {
      const a = (i / 12) * Math.PI * 2 - Math.PI / 2
      return (
        <circle key={i} cx={100 + 78 * Math.cos(a)} cy={100 + 78 * Math.sin(a)} r="5" fill="currentColor" className="glyph-stroke" style={{ transitionDelay: `${i * 60}ms` }} />
      )
    })}
    <circle cx="100" cy="100" r="30" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
    <text x="100" y="97" textAnchor="middle" fill="currentColor" fontSize="13" fontWeight="700">12</text>
    <text x="100" y="112" textAnchor="middle" fill="currentColor" fontSize="8" opacity="0.7">REVIEWS</text>
  </svg>
)

const StrategicPlanningPage = () => {
  const { i18n } = useTranslation()
  const c = getStrategicPlanningContent(i18n.language)
  const bookLabel = getCtaContent(i18n.language).close.book
  const heroRef = useReveal(0.05)
  const phasesRef = useReveal(0.08)
  const artifactsRef = useReveal(0.08)
  const cadenceRef = useReveal(0.08)

  return (
    <div className="relative isolate bg-transparent font-Quicksand text-white">
      <SiteAmbient />
      <Seo title={c.seo.title} description={c.seo.description} path="/services/strategic-planning" />

      <header className="px-6 pb-8 pt-32 md:px-16 md:pt-40">
        <div ref={heroRef} className="reveal-on-scroll mx-auto max-w-6xl">
          <p className="text-xs uppercase tracking-[0.4em] text-pyramid-19/80">{c.hero.kicker}</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-light leading-[1.05] md:text-6xl">{c.hero.headline}</h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">{c.hero.body}</p>
          <p className="mt-4 text-sm text-pyramid-19/90">{c.hero.method}</p>
        </div>
      </header>

      <main className="px-6 pb-24 md:px-16">
        <div className="mx-auto max-w-6xl space-y-16">
          {/* ── three phases as a flow ── */}
          <section ref={phasesRef} className="reveal-on-scroll">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{c.phases.kicker}</p>
            <ol className="stagger-grid is-visible mt-6 grid gap-3 md:grid-cols-3">
              {c.phases.items.map((ph, i) => (
                <li key={ph.n} className="stagger-card hover-lift relative rounded-xl border border-pyramid-19/25 bg-[#151b23] p-6" style={{ borderTopWidth: '3px' }}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-2xl font-black text-pyramid-19/60">{ph.n}</p>
                    <span className="inline-flex items-center gap-2 rounded-md border border-white/10 bg-[#1a212a] px-2.5 py-1 text-[12px] text-slate-300">
                      <span className="h-1.5 w-1.5 rounded-full bg-pyramid-19" />
                      {ph.duration}
                    </span>
                  </div>
                  <p className="mt-3 text-lg font-semibold text-white">{ph.name}</p>
                  <p className="mt-2 text-sm leading-6 text-slate-400">{ph.detail}</p>
                  {i < c.phases.items.length - 1 ? (
                    <span className="flow-dash absolute -right-3 top-1/2 hidden w-3 -translate-y-1/2 text-pyramid-19 md:block" aria-hidden="true" />
                  ) : null}
                </li>
              ))}
            </ol>
            <p className="mt-4 text-[13px] text-slate-500">{c.phases.note}</p>
          </section>

          {/* ── artifacts as a ledger ── */}
          <section ref={artifactsRef} className="reveal-on-scroll">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{c.artifacts.kicker}</p>
            <div className="mt-4 overflow-hidden rounded-xl border border-white/10">
              {c.artifacts.items.map((a, i) => (
                <div key={a.name} className={`flex flex-col gap-1 px-6 py-4 sm:flex-row sm:items-baseline sm:gap-6 ${i % 2 ? 'bg-[#151b23]' : 'bg-[#12171e]'}`}>
                  <p className="w-56 shrink-0 font-semibold text-white">{a.name}</p>
                  <p className="text-sm leading-6 text-slate-400">{a.detail}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── the year as a ring ── */}
          <section ref={cadenceRef} className="reveal-on-scroll grid items-center gap-8 rounded-2xl border border-white/10 bg-white/[0.02] p-7 md:grid-cols-[auto_1fr] md:p-10">
            <CadenceRing />
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-pyramid-19">{c.cadence.kicker}</p>
              <h2 className="mt-3 text-2xl font-light md:text-3xl">{c.cadence.title}</h2>
              <p className="mt-3 max-w-2xl leading-7 text-slate-400">{c.cadence.detail}</p>
            </div>
          </section>

          {/* ── close ── */}
          <section className="text-center">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href="/talk?i=strategy"
                onClick={() => trackEvent('strategy_talk_click')}
                className="inline-flex items-center gap-2 rounded-[10px] bg-gradient-to-r from-pyramid-1 via-pyramid-10 to-pyramid-19 px-8 py-4 font-semibold text-ebony shadow-lg shadow-pyramid-10/30 transition-all hover:-translate-y-0.5"
              >
                {c.cta.label}
                <span aria-hidden="true">&#8594;</span>
              </a>
            <a
              href="/book"
              onClick={() => trackEvent('strategy_book_click')}
              className="inline-flex items-center gap-2 rounded-[10px] border border-pyramid-19/50 px-7 py-3 font-semibold text-pyramid-19 transition-all hover:bg-pyramid-19/10"
            >
              {bookLabel}
              <span aria-hidden="true">&#8594;</span>
            </a>
            </div>
            <p className="mt-3 text-sm text-slate-500">{c.cta.sub}</p>
          </section>
        </div>
      </main>
    </div>
  )
}

export default StrategicPlanningPage
